/**
 * @fileoverview Mock backend for the Sales CRM target endpoints.
 * @description Registers GET /sales/targets/periods, GET /sales/targets/eligible-assignees and POST
 * /sales/targets (Assign Target) on the feature's shared mock adapter, answering in the backend's
 * envelopes with its error codes (Confluence 3.5.11.12-.14). Reads and writes the shared Target and
 * Tenant stores, so a target assigned here still works with every mock-only row action and on every
 * other sales page. Follows the backend's rules: a Super Admin, Sales Head or Sales Manager may
 * assign; the assignee is a Manager or Executive, never a Head, within the caller's downline (a
 * Manager only their own Executives, never themself); a batch of one to a hundred distinct accepted,
 * unarchived prospects with no open target is all or nothing, and the assignee becomes each
 * prospect's owner. Like the other sales mocks it doesn't check MFA freshness. Must load before
 * targets-mock.service.ts, whose GET /sales/targets/:id would otherwise answer the two GET routes
 * here. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'
import { listSalesRoster } from '@/features/sales/mocks/sales-roster-mock-store'

import { admissionStateOf, mockCaller } from './sales-crm-mock-projection'
import {
  bodyOf,
  failReply,
  headerOf,
  okEnvelope,
  pageReply,
  queryOf,
} from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { indiaToday, targetPeriodsFor } from './sales-target-periods.utils'
import { assignTarget, listTargets } from './targets-mock-store'
import { findTenantById } from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type {
  AssignTargetBatchRequest,
  SalesTargetPeriodType,
} from '@/features/sales/types/sales-target.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

const PERIOD_TYPES: SalesTargetPeriodType[] = ['MONTHLY', 'QUARTERLY', 'BI_ANNUAL', 'ANNUAL']
const PRIORITIES = ['HIGH', 'MEDIUM', 'LOW']
const MAX_PROSPECTS = 100

/** @description The frontend's period type and priority for each of the backend's. */
const FRONTEND_PERIOD_TYPES = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  BI_ANNUAL: 'Half-Yearly',
  ANNUAL: 'Annual',
} as const
const FRONTEND_PRIORITIES = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' } as const

/**
 * @description The Managers and Executives a caller may give a target to: any active one for a
 * Super Admin, those in their downline for a Sales Head, only the Executives in their downline for
 * a Sales Manager; never a Sales Head and never the caller.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {SalesUserRecord[]} The choices, by name.
 */
function assigneesFor(caller: SalesUserRecord): SalesUserRecord[] {
  const roster = listSalesRoster()
  const downline = caller.role === 'Super Admin' ? null : resolveDownlineUserIds(caller.id, roster)
  return roster
    .filter((user) => {
      if (user.id === caller.id || user.status !== 'Active') return false
      if (user.role !== 'Sales Manager' && user.role !== 'Sales Executive') return false
      if (caller.role === 'Sales Manager' && user.role !== 'Sales Executive') return false
      return downline === null || downline.includes(user.id)
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * @description Whether the caller may assign targets at all: a Super Admin, Sales Head or Sales
 * Manager (an Executive is refused).
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {boolean} True when they may.
 */
function mayAssign(caller: SalesUserRecord | undefined): caller is SalesUserRecord {
  return caller !== undefined && caller.role !== 'Sales Executive'
}

/**
 * @description Checks an Assign Target body's shape, in the backend's validation order.
 * @param {AssignTargetBatchRequest} body - The request body.
 * @returns {string | null} What's wrong, or null when it's valid.
 */
function invalidBatch(body: AssignTargetBatchRequest): string | null {
  const name = typeof body.name === 'string' ? body.name.trim() : ''
  if (name.length < 1 || name.length > 150) return 'The target name must be 1-150 characters.'
  if (!PERIOD_TYPES.includes(body.periodType)) return 'The period type is unknown.'
  if (!PRIORITIES.includes(body.priority)) return 'The priority is unknown.'
  if (!body.assigneeUserId) return 'An assignee is required.'
  const ids = Array.isArray(body.prospectIds) ? body.prospectIds : []
  if (ids.length < 1 || ids.length > MAX_PROSPECTS) return 'A batch holds 1-100 prospects.'
  if (new Set(ids.map((id) => id.toLowerCase())).size !== ids.length) {
    return 'The prospects must be distinct.'
  }
  const open = targetPeriodsFor(body.periodType, indiaToday()).some(
    (period) => period.periodLabel === body.periodLabel,
  )
  return open ? null : 'The period is unknown or has ended.'
}

/**
 * @description Checks every prospect of a batch before anything is written, so it is all or
 * nothing.
 * @param {string[]} prospectIds - The batch's prospects.
 * @returns {MockReply | null} The error to answer, or null when every prospect may take a target.
 */
function prospectRefusal(prospectIds: string[]): MockReply | null {
  const openTargets = new Set(listTargets({ status: 'Outstanding' }).map((t) => t.tenantId))
  for (const id of prospectIds) {
    const tenant = findTenantById(id)
    if (!tenant || tenant.status !== 'Prospect') return failReply(404, '4101', 'Prospect not found')
    if (admissionStateOf(tenant) !== 'ACCEPTED' || tenant.archived) {
      return failReply(409, '4103', 'The prospect is not accepted and active')
    }
    if (openTargets.has(id))
      return failReply(409, '4111', 'The prospect already has an open target')
  }
  return null
}

/**
 * @description Assigns the batch the way the backend does: one target per prospect, the assignee
 * made each prospect's owner.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The assigned batch, or the backend's error.
 */
function assignBatchReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  if (!mayAssign(caller)) return failReply(403, '2008', 'Forbidden')
  if (!headerOf(config, 'Idempotency-Key')) {
    return failReply(428, '0004', 'Idempotency-Key is required')
  }
  const body = bodyOf(config) as unknown as AssignTargetBatchRequest
  const invalid = invalidBatch(body)
  if (invalid) return failReply(400, '0001', invalid)
  const refusal = prospectRefusal(body.prospectIds)
  if (refusal) return refusal
  if (!assigneesFor(caller).some((user) => user.id === body.assigneeUserId)) {
    return failReply(422, '4106', 'The assignee is not eligible')
  }

  const period = targetPeriodsFor(body.periodType, indiaToday()).find(
    (candidate) => candidate.periodLabel === body.periodLabel,
  )
  if (!period) return failReply(400, '0001', 'The period is unknown or has ended.')
  const items = body.prospectIds.flatMap((prospectId) => {
    const created = assignTarget({
      tenantId: prospectId,
      name: body.name.trim(),
      ownerId: body.assigneeUserId,
      periodType: FRONTEND_PERIOD_TYPES[body.periodType],
      periodLabel: period.periodLabel,
      startDate: period.startDate,
      endDate: period.endDate,
      priority: FRONTEND_PRIORITIES[body.priority],
      actingUserId: caller.id,
    })
    return created ? [{ prospectId, targetId: created.id, tenantCode: prospectId }] : []
  })
  const result = {
    batchId: crypto.randomUUID(),
    assigneeUserId: body.assigneeUserId,
    periodType: body.periodType,
    periodLabel: period.periodLabel,
    dueDate: period.endDate,
    priority: body.priority,
    items,
  }
  return [201, { ...okEnvelope(result), httpStatus: 201 }]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/targets/periods').reply((config) => {
    const { type } = queryOf(config)
    if (!mockCaller()) return failReply(403, '2008', 'Forbidden')
    if (!type || !PERIOD_TYPES.includes(type as SalesTargetPeriodType)) {
      return failReply(400, '0001', 'The type is missing or unknown')
    }
    const today = indiaToday()
    const periods = targetPeriodsFor(type as SalesTargetPeriodType, today)
    return [200, okEnvelope({ type, today, periods })]
  })

  salesMockAdapter.onGet('/sales/targets/eligible-assignees').reply((config) => {
    const current = mockCaller()
    if (!mayAssign(current)) return failReply(403, '2008', 'Forbidden')
    const search = queryOf(config).search?.trim().toLowerCase()
    const choices = assigneesFor(current)
      .filter(
        (user) =>
          !search ||
          user.name.toLowerCase().includes(search) ||
          user.id.toLowerCase().includes(search),
      )
      .map((user) => ({
        userId: user.id,
        displayName: user.name,
        employeeCode: user.id,
        positionCode: user.role === 'Sales Manager' ? 'SALES_MANAGER' : 'SALES_EXECUTIVE',
      }))
    return pageReply(config, choices)
  })

  salesMockAdapter.onPost('/sales/targets').reply(assignBatchReply)
}
